import { useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { CountrySelect } from '../ui/CountrySelect';
import { Typography } from '../ui/Typography';

interface AgeGateScreenProps {
  onAffirm: (birthday: string, country: string) => void;
  /** Validation message to show under the fields. */
  error: string | null;
}

/** Today as YYYY-MM-DD, in local time, for the date field's upper bound. */
const todayIso = (): string => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
};

/**
 * First-launch age affirmation for the web app. Asks for a date of birth and
 * the country to judge it against — the legal drinking age runs from 16 to 21
 * depending on where someone is, so a fixed threshold is wrong somewhere.
 */
export const AgeGateScreen = ({ onAffirm, error }: AgeGateScreenProps) => {
  const [birthday, setBirthday] = useState('');
  const [country, setCountry] = useState('');

  const canSubmit = birthday.length > 0 && country.length > 0;

  return (
    <div className="min-h-screen bg-gray-50 flex items-start justify-center px-4 pt-6 pb-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <Typography variant="heading1" as="h1" className="mb-2">
            Before you start
          </Typography>
          <Typography variant="bodyMedium" className="text-gray-600">
            WineryXplorer is about wine, so we need to know that you are old
            enough to be here. Please tell us your date of birth and where you
            are.
          </Typography>
        </div>

        <Card>
          <div className="space-y-4">
            <CountrySelect
              label="Country"
              value={country}
              onChange={setCountry}
              helperText="We use this to apply the right legal drinking age."
              fullWidth
              required
            />

            <Input
              label="Date of Birth"
              type="date"
              value={birthday}
              onChange={(e) => setBirthday(e.target.value)}
              max={todayIso()}
              error={error ?? undefined}
              fullWidth
              required
            />

            <Button
              variant="primary"
              size="lg"
              fullWidth
              onClick={() => onAffirm(birthday, country)}
              disabled={!canSubmit}
            >
              Continue
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};
